import { useEffect, useState, type CSSProperties } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { CarrierCompanyInput } from '@/components/CarrierCompanyInput';
import { useFormFactor } from '@/lib/formFactor';
import type { ShipmentListItem } from './types';
import { updateShipmentLogistics } from './api';
import s from './ShipmentLogisticsDialog.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function ShipmentLogisticsDialog({ shipment, anchorElement, onClose, onSuccess }: { shipment: ShipmentListItem; anchorElement: HTMLButtonElement | null; onClose: () => void; onSuccess: () => void }) {
  const factor = useFormFactor();
  const [company, setCompany] = useState(shipment.logisticsCompany);
  const [trackingNo, setTrackingNo] = useState(shipment.trackingNo);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const changed = company.trim() !== shipment.logisticsCompany || trackingNo.trim() !== shipment.trackingNo;
  const valid = company.trim().length > 0 && company.trim().length <= 100 && trackingNo.trim().length > 0 && trackingNo.trim().length <= 150;
  const anchorRect = anchorElement?.getBoundingClientRect();
  const tabletStyle = factor === 'tablet' ? { '--tablet-popover-top': `${Math.round((anchorRect?.bottom ?? 252) + 8)}px` } as CSSProperties : undefined;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const save = async () => {
    if (!valid || !changed || saving || uncertain || READONLY) return;
    setSaving(true); setError('');
    try {
      await updateShipmentLogistics(shipment.id, {
        logisticsCompany: company.trim(), trackingNo: trackingNo.trim(),
        expectedLogisticsCompany: shipment.logisticsCompany, expectedTrackingNo: shipment.trackingNo,
      });
      onSuccess();
      onClose();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '保存失败');
    } finally { setSaving(false); }
  };

  return <div className={`${s.backdrop} ${factor === 'phone' ? s.phone : factor === 'tablet' ? s.tablet : s.desktop}`} style={tabletStyle} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal={factor !== 'tablet'} aria-label="修改物流信息">
      <header className={s.head}><button type="button" onClick={onClose} disabled={saving} aria-label="取消修改">{factor === 'phone' ? '取消' : <Icon name="x" size={18} />}</button><div><b>修改物流信息</b>{factor === 'phone' && <small>{shipment.shipmentNo}</small>}</div><span /></header>
      <div className={s.body}>
        <div className={s.notice}><Icon name="info" size={16} /><span>只能改物流公司和单号，不能改发货数量。数量错了要走异常售后。</span></div>
        <div className={s.fields}>
          <label><span><em>*</em> 物流公司</span><CarrierCompanyInput value={company} onChange={setCompany} disabled={saving || READONLY} /></label>
          <label><span><em>*</em> 物流单号</span><input className="tnum" value={trackingNo} maxLength={150} autoComplete="off" onChange={(event) => setTrackingNo(event.target.value)} placeholder="填写物流单号" /></label>
        </div>
        <p className={s.hint}>修改会记进操作日志。</p>
        {READONLY && <p className={s.hint}>当前为生产只读模式，可查看窗口，暂不能保存。</p>}
        {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先刷新发货批次，暂勿重复提交。'}</p>}
      </div>
      <footer className={s.footer}><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={!valid || !changed || saving || uncertain || READONLY} onClick={() => void save()}>{saving ? '保存中…' : '保存'}</button></footer>
    </section>
  </div>;
}
